// --------------------------------------------------------------------------
// Supabase Auth Context — replaces Clerk's useAuth/useUser hooks
// --------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabase";

interface AuthState {
  session: Session | null;
  user: User | null;
  isLoaded: boolean;
  isSignedIn: boolean;
}

interface AuthContextValue extends AuthState {
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    user: null,
    isLoaded: !supabase, // If no supabase, mark as loaded immediately (no auth)
    isSignedIn: false,
  });

  useEffect(() => {
    if (!supabase) {
      // No supabase client — auth disabled, landing page still works
      setState({ session: null, user: null, isLoaded: true, isSignedIn: false });
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState({
        session,
        user: session?.user ?? null,
        isLoaded: true,
        isSignedIn: !!session,
      });
    });

    // Listen for auth changes (sign in, sign out, token refresh)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setState({
        session,
        user: session?.user ?? null,
        isLoaded: true,
        isSignedIn: !!session,
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }, []);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    await supabase.auth.signOut();
    window.location.replace("https://peekop.com");
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, getToken, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/** Drop-in replacement for Clerk's useAuth() */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/** Drop-in replacement for Clerk's useUser() */
export function useUser() {
  const { user, isLoaded } = useAuth();

  if (!user) {
    return { user: null, isLoaded };
  }

  // Map Supabase user to a shape similar to Clerk's useUser()
  const metadata = user.user_metadata ?? {};
  return {
    user: {
      id: user.id,
      email: user.email ?? "",
      firstName: (metadata.first_name as string) ?? (metadata.full_name as string)?.split(" ")[0] ?? null,
      lastName: (metadata.last_name as string) ?? (metadata.full_name as string)?.split(" ").slice(1).join(" ") ?? null,
      fullName: (metadata.full_name as string) ?? user.email ?? "User",
      imageUrl: (metadata.avatar_url as string) ?? null,
      primaryEmailAddress: { emailAddress: user.email ?? "" },
    },
    isLoaded,
  };
}
